import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  MdArrowBack,
  MdOutlineSend,
  MdOutlineSave,
  MdOutlineScience,
  MdOutlineVisibility,
  MdOutlineSchedule,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { EMAIL } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import {
  Badge,
  Card,
  ErrorBlock,
  SectionTitle,
  SkeletonRows,
} from "../components/crm/Primitives";
import {
  SkeletonHeader,
  SkeletonPanel,
} from "../components/crm/Skeletons";
import SegmentBuilder from "../components/crm/SegmentBuilder";
import HtmlEditor from "../components/crm/HtmlEditor";

/*
  Composição de campanha.

  Três colunas de atenção, na ordem em que a pergunta aparece: PARA QUEM
  (segmento, com a contagem ao vivo), O QUE (assunto e corpo) e COMO FICA
  (prévia renderizada com rodapé e descadastro).

  A prévia mostra o HTML FINAL — o mesmo que o renderer monta no envio, com
  cabeçalho, rodapé legal e link de descadastro. Pré-visualizar só o corpo
  escondia justamente a parte que, faltando, transforma o envio em problema
  jurídico.
*/

const VARIABLE_HINT =
  "Escreva com {{nome}}, {{salao}}, {{plano}}, {{agendamentos30d}}… — a lista completa fica ao lado do assunto.";

const EmailComposerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdminOrOperator } = useApplication();

  const isEditing = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    subject: "",
    body: "",
    segment: {},
  });
  const [testEmail, setTestEmail] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef(null);

  const campaign = useQuery(
    ["email", "campaign", id],
    async () => {
      const { data } = await axios.get(`${EMAIL.CAMPAIGN}/${id}`);
      return data.data;
    },
    { enabled: isEditing }
  );

  const { data: options } = useQuery(["email", "segment-options"], async () => {
    const { data } = await axios.get(EMAIL.SEGMENT_OPTIONS);
    return data.data;
  });

  useEffect(() => {
    if (campaign.data) {
      setForm({
        name: campaign.data.name,
        subject: campaign.data.subject,
        body: campaign.data.body,
        segment: campaign.data.segment || {},
      });
    }
  }, [campaign.data]);

  const preview = useQuery(
    ["email", "preview", form.subject, form.body],
    async () => {
      const { data } = await axios.post(EMAIL.PREVIEW, {
        subject: form.subject,
        body: form.body,
      });
      return data.data;
    },
    { enabled: showPreview && Boolean(form.body.trim()) }
  );

  const onError = (requestError) =>
    setError(
      requestError.response?.data?.message || "Não foi possível concluir a ação."
    );

  const save = useMutation(
    async () => {
      if (isEditing) {
        const { data } = await axios.put(`${EMAIL.CAMPAIGN}/${id}`, form);
        return data.data;
      }
      const { data } = await axios.post(EMAIL.CAMPAIGNS, form);
      return data.data;
    },
    {
      onSuccess: (data) => {
        setError(null);
        queryClient.invalidateQueries(["email"]);
        setFeedback("Campanha salva.");
        if (!isEditing) navigate(`/emails/${data.id}/editar`, { replace: true });
      },
      onError,
    }
  );

  const sendTest = useMutation(
    async () => {
      const campaignId = id || (await save.mutateAsync()).id;
      const { data } = await axios.post(`${EMAIL.CAMPAIGN}/${campaignId}/test`, {
        email: testEmail,
      });
      return data.data;
    },
    {
      onSuccess: (data) => {
        setError(null);
        setFeedback(`E-mail de teste enviado para ${data.sentTo}.`);
      },
      onError,
    }
  );

  const start = useMutation(
    async () => {
      const saved = await save.mutateAsync();
      const { data } = await axios.post(`${EMAIL.CAMPAIGN}/${saved.id}/start`);
      return { ...data.data, id: saved.id };
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["email"]);
        navigate(`/emails/${data.id}`);
      },
      onError,
    }
  );

  if (isEditing && campaign.isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonHeader />
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <SkeletonPanel lines={7} />
          <SkeletonPanel lines={7} />
        </div>
      </div>
    );
  }

  const locked =
    isEditing && !["DRAFT", "SCHEDULED"].includes(campaign.data?.status);

  const canSend =
    form.name.trim() && form.subject.trim() && form.body.trim() && !locked;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Link
            to="/emails"
            className="mt-1 shrink-0 text-slate-400 transition hover:text-pink-600"
            title="Voltar"
          >
            <MdArrowBack size={20} />
          </Link>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900">
              {isEditing ? "Editar campanha" : "Nova campanha"}
            </h1>
            <p className="text-sm text-slate-500">
              O envio sai aos poucos, respeitando o limite do servidor de
              e-mail.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowPreview((value) => !value)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200"
          >
            <MdOutlineVisibility size={16} />
            {showPreview ? "Esconder prévia" : "Ver prévia"}
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!canSend || save.isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200 disabled:opacity-40"
          >
            <MdOutlineSave size={16} /> Salvar rascunho
          </button>
          {isAdminOrOperator ? (
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    "Iniciar o envio? Depois disso a campanha não pode mais ser editada."
                  )
                ) {
                  start.mutate();
                }
              }}
              disabled={!canSend || start.isLoading}
              className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-pink-700 disabled:opacity-40"
            >
              <MdOutlineSend size={16} />
              {start.isLoading ? "Iniciando..." : "Enviar agora"}
            </button>
          ) : null}
        </div>
      </header>

      {locked ? (
        <p className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">
          Esta campanha já foi enviada e não pode mais ser editada — o relatório
          precisa refletir o texto que as pessoas receberam.
        </p>
      ) : null}
      {error ? <ErrorBlock message={error} /> : null}
      {feedback ? (
        <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
          {feedback}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="space-y-4">
          <SectionTitle title="Para quem" hint="Mesmos filtros da tela de Clientes" />
          <Card className="p-4">
            <SegmentBuilder
              segment={form.segment}
              disabled={locked}
              onChange={(segment) => setForm((prev) => ({ ...prev, segment }))}
            />
          </Card>

          {showPreview ? (
            <>
              <SectionTitle
                title="Como chega"
                hint="HTML final, com rodapé e link de descadastro"
                action={
                  preview.data ? (
                    <Badge tone="neutral">{preview.data.subject}</Badge>
                  ) : null
                }
              />
              <Card className="overflow-hidden">
                {preview.isLoading ? (
                  <SkeletonRows rows={4} className="p-4" />
                ) : preview.data ? (
                  /*
                    A prévia vai num iframe com sandbox. O corpo é HTML escrito
                    à mão pela equipe e renderizado com dados do cliente;
                    injetá-lo direto no DOM do painel daria a ele acesso ao
                    token da sessão.
                  */
                  <iframe
                    title="Prévia do e-mail"
                    sandbox=""
                    srcDoc={preview.data.html}
                    className="h-[560px] w-full border-0 bg-slate-100"
                  />
                ) : (
                  <p className="px-4 py-8 text-center text-sm text-slate-400">
                    Escreva o conteúdo para ver a prévia.
                  </p>
                )}
              </Card>
            </>
          ) : null}
        </section>

        <section className="space-y-4">
          <SectionTitle title="O que escrever" hint={VARIABLE_HINT} />
          <Card className="space-y-3 p-4">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Nome interno da campanha
              </label>
              <input
                disabled={locked}
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                placeholder="Ex.: Conversão free ativo — outubro"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Assunto
              </label>
              <input
                disabled={locked}
                value={form.subject}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, subject: event.target.value }))
                }
                placeholder="{{nome}}, seu salão cresceu este mês"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Conteúdo
              </label>
              <HtmlEditor
                ref={editorRef}
                value={form.body}
                disabled={locked}
                onChange={(body) => setForm((prev) => ({ ...prev, body }))}
                placeholder="Escreva aqui, ou troque para HTML e cole um e-mail pronto…"
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              {(options?.variables || []).map((variable) => (
                <button
                  key={variable.key}
                  type="button"
                  disabled={locked}
                  title={`${variable.label} — ex.: ${variable.example}`}
                  onClick={() => editorRef.current?.inserirVariavel(variable.key)}
                  className="rounded-full border border-slate-200 px-2 py-0.5 font-mono text-[11px] text-slate-600 transition hover:border-pink-300 hover:text-pink-700 disabled:opacity-40"
                >
                  {`{{${variable.key}}}`}
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-4">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              <MdOutlineScience size={14} /> Enviar teste
            </p>
            {/*
              O teste usa dados de EXEMPLO, nunca os de um cliente real: um
              teste que puxa a primeira empresa do segmento acaba mandando o
              nome de um cliente de verdade para quem está testando.
            */}
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(event) => setTestEmail(event.target.value)}
                placeholder="seu@email.com"
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300"
              />
              <button
                type="button"
                onClick={() => sendTest.mutate()}
                disabled={!testEmail.includes("@") || sendTest.isLoading || !canSend}
                className="rounded-xl border border-pink-200 px-3 py-2 text-xs font-semibold text-pink-700 transition hover:bg-pink-50 disabled:opacity-40"
              >
                {sendTest.isLoading ? "Enviando..." : "Enviar teste"}
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-slate-400">
              Vai com dados de exemplo (Duda / Studio Bella), não com os de um
              cliente real.
            </p>
          </Card>
        </section>
      </div>

      {isAdminOrOperator && !locked ? (
        <p className="flex items-center gap-1.5 text-xs text-slate-500">
          <MdOutlineSchedule size={14} />
          Para agendar, salve o rascunho e use o botão de agendamento na lista
          de campanhas.
        </p>
      ) : null}
    </div>
  );
};

export default EmailComposerPage;
